/**
 * SchedulePicker Component
 * Allows users to configure when a workflow should run
 */

import { useState, useEffect } from 'preact/hooks';

interface SchedulePickerProps {
  schedule: {
    type: 'one-off' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'cron';
    expression?: string;
    executeAt?: string;
    timezone: string;
  };
  onChange: (schedule: {
    type: 'one-off' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'cron';
    expression?: string;
    executeAt?: string;
    timezone: string;
  }) => void;
}

export function SchedulePicker({ schedule, onChange }: SchedulePickerProps) {
  // Parse time from cron expression if daily/weekly
  const parseTimeFromCron = (expression?: string): { hour: string; minute: string } => {
    if (!expression) return { hour: '09', minute: '00' };
    const parts = expression.split(' ');
    if (parts.length >= 2) {
      return {
        minute: parts[0].padStart(2, '0'),
        hour: parts[1].padStart(2, '0'),
      };
    }
    return { hour: '09', minute: '00' };
  };

  // Parse day of week from cron expression if weekly
  const parseDayFromCron = (expression?: string): string => {
    if (!expression) return '1'; // Monday
    const parts = expression.split(' ');
    if (parts.length >= 5) {
      return parts[4] || '1';
    }
    return '1';
  };

  const [dailyTime, setDailyTime] = useState(parseTimeFromCron(schedule.expression));
  const [weeklyDay, setWeeklyDay] = useState(parseDayFromCron(schedule.expression));
  const [weeklyTime, setWeeklyTime] = useState(parseTimeFromCron(schedule.expression));
  const [monthlyDay, setMonthlyDay] = useState('1'); // Day of month (1-31)
  const [monthlyTime, setMonthlyTime] = useState(parseTimeFromCron(schedule.expression));

  // Update local state when schedule.expression changes
  useEffect(() => {
    if (schedule.type === 'daily') {
      setDailyTime(parseTimeFromCron(schedule.expression));
    } else if (schedule.type === 'weekly') {
      setWeeklyTime(parseTimeFromCron(schedule.expression));
      setWeeklyDay(parseDayFromCron(schedule.expression));
    } else if (schedule.type === 'monthly' && schedule.expression) {
      setMonthlyTime(parseTimeFromCron(schedule.expression));
      const parts = schedule.expression.split(' ');
      if (parts.length >= 3) {
        setMonthlyDay(parts[2] || '1');
      }
    }
  }, [schedule.expression, schedule.type]);

  // Generate expression on mount if missing (for daily/weekly/hourly/monthly schedules)
  useEffect(() => {
    console.log('[SchedulePicker] useEffect running - schedule:', schedule);
    console.log('[SchedulePicker] dailyTime:', dailyTime, 'weeklyTime:', weeklyTime, 'weeklyDay:', weeklyDay);

    if (schedule.type === 'hourly' && !schedule.expression) {
      const expression = '0 * * * *'; // Every hour at minute 0
      console.log('[SchedulePicker] Generating hourly expression:', expression);
      onChange({
        ...schedule,
        expression,
      });
    } else if (schedule.type === 'daily' && !schedule.expression) {
      const expression = `${dailyTime.minute} ${dailyTime.hour} * * *`;
      console.log('[SchedulePicker] Generating daily expression:', expression);
      onChange({
        ...schedule,
        expression,
      });
    } else if (schedule.type === 'weekly' && !schedule.expression) {
      const expression = `${weeklyTime.minute} ${weeklyTime.hour} * * ${weeklyDay}`;
      console.log('[SchedulePicker] Generating weekly expression:', expression);
      onChange({
        ...schedule,
        expression,
      });
    } else if (schedule.type === 'monthly' && !schedule.expression) {
      const expression = `${monthlyTime.minute} ${monthlyTime.hour} ${monthlyDay} * *`;
      console.log('[SchedulePicker] Generating monthly expression:', expression);
      onChange({
        ...schedule,
        expression,
      });
    } else {
      console.log('[SchedulePicker] No expression generation needed - type:', schedule.type, 'expression:', schedule.expression);
    }
  }, [schedule.type, schedule.expression, dailyTime, weeklyTime, weeklyDay, monthlyDay, monthlyTime, onChange]);

  const handleTypeChange = (type: 'one-off' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'cron') => {
    let expression: string | undefined;

    // Generate default cron expressions for hourly/daily/weekly/monthly
    if (type === 'hourly') {
      expression = '0 * * * *'; // Every hour at minute 0
    } else if (type === 'daily') {
      expression = `${dailyTime.minute} ${dailyTime.hour} * * *`;
    } else if (type === 'weekly') {
      expression = `${weeklyTime.minute} ${weeklyTime.hour} * * ${weeklyDay}`;
    } else if (type === 'monthly') {
      expression = `${monthlyTime.minute} ${monthlyTime.hour} ${monthlyDay} * *`;
    }

    onChange({
      ...schedule,
      type,
      // Clear fields that don't apply to new type
      expression: expression || (type === 'cron' ? schedule.expression : undefined),
      executeAt: type === 'one-off' ? schedule.executeAt : undefined,
    });
  };

  const handleExecuteAtChange = (executeAt: string) => {
    onChange({
      ...schedule,
      executeAt,
    });
  };

  const handleExpressionChange = (expression: string) => {
    onChange({
      ...schedule,
      expression,
    });
  };

  const handleTimezoneChange = (timezone: string) => {
    onChange({
      ...schedule,
      timezone,
    });
  };

  const handleDailyTimeChange = (hour: string, minute: string) => {
    setDailyTime({ hour, minute });
    onChange({
      ...schedule,
      expression: `${minute} ${hour} * * *`,
    });
  };

  const handleWeeklyDayChange = (day: string) => {
    setWeeklyDay(day);
    onChange({
      ...schedule,
      expression: `${weeklyTime.minute} ${weeklyTime.hour} * * ${day}`,
    });
  };

  const handleWeeklyTimeChange = (hour: string, minute: string) => {
    setWeeklyTime({ hour, minute });
    onChange({
      ...schedule,
      expression: `${minute} ${hour} * * ${weeklyDay}`,
    });
  };

  const handleMonthlyDayChange = (day: string) => {
    setMonthlyDay(day);
    onChange({
      ...schedule,
      expression: `${monthlyTime.minute} ${monthlyTime.hour} ${day} * *`,
    });
  };

  const handleMonthlyTimeChange = (hour: string, minute: string) => {
    setMonthlyTime({ hour, minute });
    onChange({
      ...schedule,
      expression: `${minute} ${hour} ${monthlyDay} * *`,
    });
  };

  // Get user's timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Common timezones
  const commonTimezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
    'UTC',
  ];

  // Include user's timezone if not in common list
  const timezoneOptions = commonTimezones.includes(userTimezone)
    ? commonTimezones
    : [userTimezone, ...commonTimezones];

  return (
    <div class="space-y-5">
      <div class="form-control">
        <label class="label">
          <span class="label-text font-semibold text-base">Schedule Type</span>
        </label>
        <select
          class="select select-bordered w-full"
          value={schedule.type}
          onChange={(e) => handleTypeChange((e.target as HTMLSelectElement).value as any)}
        >
          <option value="one-off">One-time</option>
          <option value="hourly">Hourly</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="cron">Custom (Cron)</option>
        </select>
      </div>

      {schedule.type === 'one-off' && (
        <div class="form-control">
          <label class="label">
            <span class="label-text font-semibold text-base">
              Execute At <span class="text-error">*</span>
            </span>
          </label>
          <input
            type="datetime-local"
            class="input input-bordered w-full"
            value={schedule.executeAt || ''}
            onChange={(e) => handleExecuteAtChange((e.target as HTMLInputElement).value)}
          />
          <label class="label">
            <span class="label-text-alt text-base-content/70">When should this workflow run?</span>
          </label>
        </div>
      )}

      {schedule.type === 'cron' && (
        <div class="form-control">
          <label class="label">
            <span class="label-text font-semibold text-base">
              Cron Expression <span class="text-error">*</span>
            </span>
          </label>
          <input
            type="text"
            class="input input-bordered font-mono w-full"
            placeholder="0 9 * * *"
            value={schedule.expression || ''}
            onChange={(e) => handleExpressionChange((e.target as HTMLInputElement).value)}
          />
          <label class="label">
            <span class="label-text-alt text-base-content/70">
              Format: minute hour day month weekday (e.g., "0 9 * * *" = 9am daily)
            </span>
          </label>
        </div>
      )}

      {schedule.type === 'hourly' && (
        <div class="alert alert-info shadow-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            class="stroke-current shrink-0 w-6 h-6"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>This workflow will run every hour</span>
        </div>
      )}

      {schedule.type === 'daily' && (
        <div class="space-y-3">
          <div class="alert alert-info shadow-sm">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              class="stroke-current shrink-0 w-6 h-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>This workflow will run once per day at the specified time</span>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-semibold text-base">
                Time <span class="text-error">*</span>
              </span>
            </label>
            <div class="flex gap-3 items-center">
              <div class="flex-1">
                <label class="label">
                  <span class="label-text-alt">Hour</span>
                </label>
                <select
                  class="select select-bordered w-full"
                  value={dailyTime.hour}
                  onChange={(e) => handleDailyTimeChange((e.target as HTMLSelectElement).value, dailyTime.minute)}
                >
                  {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
              <div class="flex-1">
                <label class="label">
                  <span class="label-text-alt">Minute</span>
                </label>
                <select
                  class="select select-bordered w-full"
                  value={dailyTime.minute}
                  onChange={(e) => handleDailyTimeChange(dailyTime.hour, (e.target as HTMLSelectElement).value)}
                >
                  {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <label class="label">
              <span class="label-text-alt text-base-content/70">
                Time is in 24-hour format (00:00 - 23:59)
              </span>
            </label>
          </div>
        </div>
      )}

      {schedule.type === 'weekly' && (
        <div class="space-y-3">
          <div class="alert alert-info shadow-sm">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              class="stroke-current shrink-0 w-6 h-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>This workflow will run once per week on the specified day and time</span>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-semibold text-base">
                Day of Week <span class="text-error">*</span>
              </span>
            </label>
            <select
              class="select select-bordered w-full"
              value={weeklyDay}
              onChange={(e) => handleWeeklyDayChange((e.target as HTMLSelectElement).value)}
            >
              <option value="0">Sunday</option>
              <option value="1">Monday</option>
              <option value="2">Tuesday</option>
              <option value="3">Wednesday</option>
              <option value="4">Thursday</option>
              <option value="5">Friday</option>
              <option value="6">Saturday</option>
            </select>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-semibold text-base">
                Time <span class="text-error">*</span>
              </span>
            </label>
            <div class="flex gap-3 items-center">
              <div class="flex-1">
                <label class="label">
                  <span class="label-text-alt">Hour</span>
                </label>
                <select
                  class="select select-bordered w-full"
                  value={weeklyTime.hour}
                  onChange={(e) => handleWeeklyTimeChange((e.target as HTMLSelectElement).value, weeklyTime.minute)}
                >
                  {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
              <div class="flex-1">
                <label class="label">
                  <span class="label-text-alt">Minute</span>
                </label>
                <select
                  class="select select-bordered w-full"
                  value={weeklyTime.minute}
                  onChange={(e) => handleWeeklyTimeChange(weeklyTime.hour, (e.target as HTMLSelectElement).value)}
                >
                  {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <label class="label">
              <span class="label-text-alt text-base-content/70">
                Time is in 24-hour format (00:00 - 23:59)
              </span>
            </label>
          </div>
        </div>
      )}

      {schedule.type === 'monthly' && (
        <div class="space-y-3">
          <div class="alert alert-info shadow-sm">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              class="stroke-current shrink-0 w-6 h-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>This workflow will run once per month on the specified day and time</span>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-semibold text-base">
                Day of Month <span class="text-error">*</span>
              </span>
            </label>
            <select
              class="select select-bordered w-full"
              value={monthlyDay}
              onChange={(e) => handleMonthlyDayChange((e.target as HTMLSelectElement).value)}
            >
              {Array.from({ length: 31 }, (_, i) => (i + 1).toString()).map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text font-semibold text-base">
                Time <span class="text-error">*</span>
              </span>
            </label>
            <div class="flex gap-3 items-center">
              <div class="flex-1">
                <label class="label">
                  <span class="label-text-alt">Hour</span>
                </label>
                <select
                  class="select select-bordered w-full"
                  value={monthlyTime.hour}
                  onChange={(e) => handleMonthlyTimeChange((e.target as HTMLSelectElement).value, monthlyTime.minute)}
                >
                  {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
              <div class="flex-1">
                <label class="label">
                  <span class="label-text-alt">Minute</span>
                </label>
                <select
                  class="select select-bordered w-full"
                  value={monthlyTime.minute}
                  onChange={(e) => handleMonthlyTimeChange(monthlyTime.hour, (e.target as HTMLSelectElement).value)}
                >
                  {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <label class="label">
              <span class="label-text-alt text-base-content/70">
                Time is in 24-hour format (00:00 - 23:59)
              </span>
            </label>
          </div>
        </div>
      )}

      <div class="form-control">
        <label class="label">
          <span class="label-text font-semibold text-base">Timezone</span>
        </label>
        <select
          class="select select-bordered w-full"
          value={schedule.timezone}
          onChange={(e) => handleTimezoneChange((e.target as HTMLSelectElement).value)}
        >
          {timezoneOptions.map((tz) => (
            <option key={tz} value={tz}>
              {tz} {tz === userTimezone ? '(Your timezone)' : ''}
            </option>
          ))}
        </select>
        <label class="label">
          <span class="label-text-alt text-base-content/70">Times will be interpreted in this timezone</span>
        </label>
      </div>
    </div>
  );
}
